import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { useAuth } from '../../../contexts/AuthContext';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface DetectionData {
  timestamp: string;
  detectionCount: number;
  detectionType: string;
  reason?: string;
  deviceName?: string;
}

interface ClassDetectionTabProps {
  deviceId: string;
  includeChildren?: boolean;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

const ClassDetectionTab: React.FC<ClassDetectionTabProps> = ({ deviceId, includeChildren = false }) => {
  const auth = useAuth();
  const accessToken = auth.tokens?.accessToken;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detectionData, setDetectionData] = useState<DetectionData[]>([]);
  const [timeRange, setTimeRange] = useState<string>('24h');
  const [viewMode, setViewMode] = useState<string>('chart');
  
  const authHeader = useMemo(() => ({ Authorization: `Bearer ${accessToken}` }), [accessToken]);

  const loadDetectionData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ skip: '0', take: '1000', sortBy: 'dateCreated', sortOrder: 'desc' });
      if (includeChildren) params.append('includeChildren', 'true');
      const res = await fetch(
        `/api/devices/${deviceId}/events?${params}`, 
        { headers: authHeader }
      );
      if (!res.ok) throw new Error('Failed to fetch events');
      
      const responseData = await res.json();
      const events = responseData.data || [];
      
      // Filter and parse detection events
      const detections: DetectionData[] = [];
      
      for (const event of events) {
        try {
          let parsedData: any = null;
          
          if (event.name) {
            try {
              parsedData = JSON.parse(event.name);
            } catch {
              // Not JSON, skip
            }
          }
          
          // Check if it has detection data
          if (parsedData && parsedData.data && parsedData.data.detection_type) {
            detections.push({
              timestamp: event.dateCreated,
              detectionCount: parsedData.data.detection_count || parsedData.count || 0,
              detectionType: parsedData.data.detection_type,
              reason: parsedData.data.reason,
              deviceName: event.deviceName || undefined,
            });
          }
        } catch (e) {
          continue;
        }
      }
      
      // Filter by time range
      const now = new Date();
      const filtered = detections.filter(item => {
        const itemDate = new Date(item.timestamp);
        const diff = now.getTime() - itemDate.getTime();
        
        switch (timeRange) {
          case '1h':
            return diff <= 60 * 60 * 1000;
          case '6h':
            return diff <= 6 * 60 * 60 * 1000;
          case '24h':
            return diff <= 24 * 60 * 60 * 1000;
          case '7d':
            return diff <= 7 * 24 * 60 * 60 * 1000;
          case '30d':
            return diff <= 30 * 24 * 60 * 60 * 1000;
          default:
            return true;
        }
      });
      
      // Sort by timestamp descending
      filtered.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      
      setDetectionData(filtered);
    } catch (e: any) {
      setError(e.message || 'Failed to load detection data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDetectionData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, timeRange, includeChildren]);

  // Aggregate detection counts by type
  const detectionSummary = useMemo(() => {
    const summary: Record<string, number> = {};
    detectionData.forEach(item => {
      summary[item.detectionType] = (summary[item.detectionType] || 0) + item.detectionCount;
    });
    return Object.entries(summary).map(([type, count]) => ({
      type,
      count,
    }));
  }, [detectionData]);

  // Time series data for line chart - chronological order
  const chartData = useMemo(() => {
    // Sort by timestamp ascending for chart
    const sorted = [...detectionData].sort((a, b) => 
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
    
    return sorted.map(item => ({
      timestamp: new Date(item.timestamp).toLocaleString(),
      fullTimestamp: item.timestamp,
      count: item.detectionCount,
      type: item.detectionType,
      reason: item.reason,
    }));
  }, [detectionData]);

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Class Detection</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel>View</InputLabel>
            <Select
              value={viewMode}
              label="View"
              onChange={(e) => setViewMode(e.target.value)}
            >
              <MenuItem value="chart">Charts</MenuItem>
              <MenuItem value="table">Table</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel>Time Range</InputLabel>
            <Select
              value={timeRange}
              label="Time Range"
              onChange={(e) => setTimeRange(e.target.value)}
            >
              <MenuItem value="1h">Last Hour</MenuItem>
              <MenuItem value="6h">Last 6 Hours</MenuItem>
              <MenuItem value="24h">Last 24 Hours</MenuItem>
              <MenuItem value="7d">Last 7 Days</MenuItem>
              <MenuItem value="30d">Last 30 Days</MenuItem>
              <MenuItem value="all">All Time</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : detectionData.length === 0 ? (
        <Alert severity="info">
          No detection data available for this device in the selected time range.
        </Alert>
      ) : viewMode === 'chart' ? (
        <Box>
          {/* Line Chart - Detection Trend Over Time */}
          <Box>
            <ResponsiveContainer width="100%" height={500}>
              <LineChart
                data={chartData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="timestamp" 
                  label={{ value: 'Time', position: 'insideBottom', offset: -5 }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  tick={{ fontSize: '0.75rem' }}
                />
                <YAxis 
                  label={{ value: 'Detection Count', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip 
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <Box sx={{ bgcolor: 'background.paper', p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                          <Typography variant="body2"><strong>Time:</strong> {formatTimestamp(data.fullTimestamp)}</Typography>
                          <Typography variant="body2"><strong>Count:</strong> {data.count}</Typography>
                          <Typography variant="body2"><strong>Type:</strong> {data.type}</Typography>
                          {data.reason && <Typography variant="body2"><strong>Reason:</strong> {data.reason}</Typography>}
                        </Box>
                      );
                    }
                    return null;
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="count" 
                  stroke="#8884d8" 
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                  name="Detection Count"
                />
              </LineChart>
            </ResponsiveContainer>
            
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Showing {detectionData.length} detection events
              </Typography>
            </Box>
          </Box>
        </Box>
      ) : (
        // Table View
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {includeChildren && <TableCell>Device</TableCell>}
                <TableCell>Timestamp</TableCell>
                <TableCell>Detection Type</TableCell>
                <TableCell align="right">Count</TableCell>
                <TableCell>Reason</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {detectionData.map((row, idx) => (
                <TableRow key={idx} hover>
                  {includeChildren && (
                    <TableCell>
                      <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 500, fontSize: '0.8rem' }}>
                        {row.deviceName || '—'}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell>{formatTimestamp(row.timestamp)}</TableCell>
                  <TableCell>
                    <Chip label={row.detectionType} size="small" color="primary" />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body1" fontWeight="bold">
                      {row.detectionCount}
                    </Typography>
                  </TableCell>
                  <TableCell>{row.reason || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ClassDetectionTab;
